import { Fragment, useEffect, useRef, useState } from "react";
import { IconImprimir } from "./icons.jsx";
import { imprimirFormato } from "../utils/imprimir-formato.jsx";
import { descargarPdf, imprimirDocumentoRapido } from "../utils/pdf.js";

const fechaEs = (f) => (f ? new Date(f).toLocaleDateString("es-ES") : "");
const dirTexto = (d) => [d?.calle, d?.cp, d?.ciudad, d?.provincia].filter(Boolean).join(", ");

// Los trabajos se apuntan en renglones sueltos; al imprimir se juntan los
// del mismo párrafo con "·" para que el texto fluya en línea y no llene la
// caja de arriba a abajo dejando media vacía. Los párrafos se respetan.
const compactarRenglones = (texto) =>
  String(texto ?? "")
    .split(/\n\s*\n+/)
    .map((p) =>
      p
        .split(/\n+/)
        .map((l) => l.trim())
        .filter(Boolean)
        .join(" · ")
    )
    .filter(Boolean)
    .join("\n");

async function cargarPlantillaEntrada() {
  try {
    const r = await fetch("/api/formatos/default/entrada-taller");
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

export async function imprimirHojaEntrada(o) {
  const plantilla = await cargarPlantillaEntrada();
  if (!plantilla) return false;
  const emp = await fetch("/api/empresa").then((r) => (r.ok ? r.json() : {})).catch(() => ({}));

  const textoSeguro = o.aseguradora
    ? `Compañía: ${o.aseguradora}${o.numeroSiniestro ? ` · Siniestro: ${o.numeroSiniestro}` : ""}${Number(o.franquicia) > 0 ? ` · Franquicia a cargo del cliente: ${Number(o.franquicia).toFixed(2)} € (IVA incluido)` : ""}`
    : Number(o.franquicia) > 0
      ? `Franquicia a cargo del cliente: ${Number(o.franquicia).toFixed(2)} € (IVA incluido)`
      : "";

  const formData = {
    "taller.nombre": emp.nombre,
    "taller.cif": emp.nif,
    "taller.direccion": dirTexto(emp.direccion),
    "taller.mail": emp.email,
    "taller.telefono": emp.telefono,
    "resguardo.numero": o.numero,
    "cliente.titular": o.cliente?.nombre ?? o.clienteNombre,
    "cliente.cifTitular": o.cliente?.nif,
    "cliente.direccion": dirTexto(o.cliente?.direccion),
    "cliente.telefono": o.cliente?.telefono ?? o.telefono,
    "cliente.mail": o.cliente?.email,
    "vehiculo.fecha": fechaEs(o.fechaEntrada),
    "vehiculo.matricula": o.matricula,
    "vehiculo.marca": o.vehiculo?.marca,
    "vehiculo.modelo": o.vehiculo?.modelo,
    "vehiculo.km": o.km != null ? Number(o.km).toLocaleString("es-ES") : "",
    "vehiculo.seguroSi": Boolean(o.aseguradora),
    "vehiculo.seguroNo": !o.aseguradora,
    // Bloque grande de la hoja: la avería / trabajos que pide el cliente.
    "vehiculo.descripcion": compactarRenglones(o.motivo),
    "seguro.detalle": textoSeguro,
    // Compat con plantillas antiguas: allí el motivo y el seguro compartían
    // el cuadro pequeño de observaciones.
    "vehiculo.observaciones": [compactarRenglones(o.motivo), textoSeguro].filter(Boolean).join(". "),
    "entrega.fechaPrevista": fechaEs(o.fechaEntregaPrevista),
  };

  const tabla = plantilla.elements.find((e) => e.type === "table");
  if (tabla) {
    (o.lineas ?? []).slice(0, tabla.rows).forEach((l, r) => {
      formData[`tbl_${tabla.id}_${r}_0`] = l.descripcion ?? "";
      // Valores reales si los hay; compat con el formato antiguo (tipo → "X").
      formData[`tbl_${tabla.id}_${r}_1`] = l.manoObra ?? (l.tipo === "mano_obra" ? "X" : "");
      formData[`tbl_${tabla.id}_${r}_2`] = l.materiales ?? (l.tipo === "material" ? "X" : "");
    });
  }

  const signatures = {};
  if (o.recepcionDigital?.firma?.imagen) {
    signatures.cliente = o.recepcionDigital.firma.imagen;
  }

  const conTitulo = (texto) => ({
    ...plantilla,
    elements: plantilla.elements.map((el) =>
      el.type === "text" && /EJEMPLAR PARA/i.test(el.text ?? "") ? { ...el, text: texto } : el
    ),
  });
  const paginas = [
    conTitulo("EJEMPLAR PARA EL PRESTADOR DEL SERVICIO"),
    conTitulo("EJEMPLAR PARA EL CLIENTE"),
  ];

  imprimirFormato(paginas, formData, signatures);
  return true;
}

export default function MenuImprimirOrden({ orden, pequeno = false }) {
  const [abierto, setAbierto] = useState(false);
  const [formatos, setFormatos] = useState([]);
  const ref = useRef(null);

  // Diseños de parte que tiene la empresa (p. ej. "Parte de trabajo" y
  // "Trabajos a realizar"): se cargan la primera vez que se abre el menú.
  useEffect(() => {
    if (!abierto || formatos.length) return;
    fetch("/api/formatos/por-tipo/parte-taller")
      .then((r) => (r.ok ? r.json() : []))
      .then((docs) => Array.isArray(docs) && docs.length && setFormatos(docs))
      .catch(() => {});
  }, [abierto, formatos.length]);

  useEffect(() => {
    if (!abierto) return;
    const cerrar = (e) => {
      if (!ref.current?.contains(e.target)) setAbierto(false);
    };
    document.addEventListener("mousedown", cerrar);
    return () => document.removeEventListener("mousedown", cerrar);
  }, [abierto]);

  function elegir(variante, formatoId = "") {
    setAbierto(false);
    if (variante === "orden-pdf") descargarPdf("parte-taller", orden._id, orden.numero, formatoId);
    else imprimirDocumentoRapido("parte-taller", orden._id, formatoId);
  }

  // El formato por defecto mantiene el nombre de siempre; los diseños
  // adicionales de la empresa se muestran con su nombre ("Trabajos a realizar").
  const lista = formatos.length
    ? formatos
    : [{ _id: "", nombre: "Orden de trabajo", porDefecto: true }];
  const etiqueta = (f) => (f.porDefecto ? "Orden de trabajo" : f.nombre);

  return (
    <span ref={ref} className="relative inline-block align-middle">
      <button
        type="button"
        onClick={() => setAbierto((a) => !a)}
        title="Imprimir orden"
        className={
          pequeno
            ? "inline-flex items-center gap-0.5 text-[0.6875rem] text-slate-400 hover:text-accent transition-colors"
            : "inline-flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:text-accent hover:bg-accent/10 transition-colors"
        }
      >
        <IconImprimir />
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {abierto && (
        <span className="absolute right-0 top-full z-40 mt-1 w-52 rounded-lg border border-slate-200 bg-white shadow-xl py-1 text-left">
          {lista.map((f) => (
            <Fragment key={f._id || "defecto"}>
              <button
                type="button"
                onClick={() => elegir("orden", f._id)}
                className="block w-full px-3.5 py-2 text-xs text-slate-700 hover:bg-accent/10 hover:text-accent"
              >
                {etiqueta(f)}
              </button>
              <button
                type="button"
                onClick={() => elegir("orden-pdf", f._id)}
                className="block w-full px-3.5 py-2 text-xs text-slate-700 hover:bg-accent/10 hover:text-accent"
              >
                {etiqueta(f)} PDF
              </button>
            </Fragment>
          ))}
        </span>
      )}
    </span>
  );
}
